/* eslint-disable prettier/prettier */
import { useState, useEffect } from 'react';
import { CollapsibleSection } from '../CollapsibleSection';
import { KeyValuePair } from '../../ui/KeyValuePair';
import { emojiIconMap } from '../../../utils/emojiIconMap';
import { TreatyEntityType } from '../../../types/TreatyEntityType';
import { CedantNameEditor } from '../../ui/CedantNameEditor';
import { FileService } from '../../../services/file.service';
import { Trash2, Plus, Save, Loader2, X } from 'lucide-react';
import { CustomSelect } from '../../ui/Select';

export const CedantsSection = ({
  cedants,
  treatyId,
  refreshTreatyData,
}: {
  cedants: any[];
  treatyId: string;
  refreshTreatyData: () => void;
}) => {
  const [editingCedants, setEditingCedants] = useState<{ [key: string]: boolean }>({});
  const [loadingCedants, setLoadingCedants] = useState<{ [key: string]: boolean }>({});
  const [allCedants, setAllCedants] = useState<any[]>([]);
  const [selectedCedantId, setSelectedCedantId] = useState<string | undefined>();
  const [isAdding, setIsAdding] = useState(false);
  const [isAddingLoading, setIsAddingLoading] = useState(false);

  useEffect(() => {
    FileService.getCedants()
      .then((res) => setAllCedants(res.data))
      .catch(() => console.error('Failed to fetch cedants'));
  }, []);

  const handleSave = async (cedantId: string) => {
    setLoadingCedants((prev) => ({ ...prev, [cedantId]: true }));
    try {
      setEditingCedants((prev) => ({ ...prev, [cedantId]: false }));
      await refreshTreatyData();
    } finally {
      setLoadingCedants((prev) => ({ ...prev, [cedantId]: false }));
    }
  };

  const handleCancel = (cedantId: string) => {
    setEditingCedants((prev) => ({ ...prev, [cedantId]: false }));
  };

  const handleEdit = (cedantId: string) => {
    setEditingCedants((prev) => ({ ...prev, [cedantId]: true }));
  };

  const handleAddCedant = async () => {
    if (!selectedCedantId) return;
    if (cedants.some((c) => c.id === selectedCedantId)) {
      alert('Cedant already added');
      return;
    }
    try {
      setIsAddingLoading(true);
      await FileService.addCedantToTreaty(treatyId, selectedCedantId);
      setSelectedCedantId(undefined);
      setIsAdding(false);
      await refreshTreatyData();
    } catch {
      console.error('Failed to add cedant');
    } finally {
      setIsAddingLoading(false);
    }
  };

  const handleRemoveCedant = async (cedantId: string) => {
    setLoadingCedants((prev) => ({ ...prev, [cedantId]: true }));
    try {
      await FileService.removeCedantFromTreaty(treatyId, cedantId);
      await refreshTreatyData();
    } catch {
      console.error('Failed to remove cedant');
    } finally {
      setLoadingCedants((prev) => ({ ...prev, [cedantId]: false }));
    }
  };

  const selectOptions = [...allCedants]
    .filter((c) => !cedants.some((existing) => existing.id === c.id))
    .map((c) => ({
      label: c.name?.trim() || '',
      value: c.id,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

  return (
    <CollapsibleSection
      title={cedants.length > 1 ? 'Cedants' : 'Cedant'}
      icon={emojiIconMap['Cedant']}
    >
      {cedants.map((cedant, index) => (
        <CollapsibleSection
          key={cedant.id}
          title={`Cedant ${index + 1}`}
          isCedant={true}
          onEdit={() => handleEdit(cedant.id)}
          isEditing={editingCedants[cedant.id] || false}
          onCancel={() => handleCancel(cedant.id)}
          extraAction={
            loadingCedants[cedant.id] ? (
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
            ) : (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveCedant(cedant.id);
                }}
                className="cursor-pointer"
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </div>
            )
          }
        >
          {editingCedants[cedant.id] ? (
            <CedantNameEditor
              cedantId={cedant.id}
              currentName={cedant.name}
              onSave={() => handleSave(cedant.id)}
              onCancel={() => handleCancel(cedant.id)}
              treatyId={treatyId}
            />
          ) : (
            <>
              <KeyValuePair
                label="Cedant Name"
                value={cedant.name}
                editable={false}
                entityId={cedant.id}
                entityType={TreatyEntityType.CEDANT}
                fieldKey="cedantName"
              />
              <KeyValuePair
                label="Country"
                value={cedant.country}
                editable={false}
                entityId={cedant.id}
                entityType={TreatyEntityType.CEDANT}
                fieldKey="cedantLocation"
              />
              <KeyValuePair
                label="Location"
                value={cedant.location}
                editable={false}
                entityId={cedant.id}
                entityType={TreatyEntityType.CEDANT}
                fieldKey="cedantDomicile"
              />
            </>
          )}
        </CollapsibleSection>
      ))}

      <div className="flex items-center justify-end gap-2 mt-2">
        {isAdding ? (
          <>
            <CustomSelect
              key={selectedCedantId} 
              data={selectOptions}
              value={selectedCedantId ?? undefined}
              onChange={(id) => setSelectedCedantId(id)}
              placeholder="Select Cedant"
            />
            <button onClick={handleAddCedant} disabled={isAddingLoading}>
              {isAddingLoading ? (
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
              ) : (
                <Save className="w-5 h-5 text-primary" />
              )}
            </button>
            <button
              onClick={() => {
                setIsAdding(false);
                setSelectedCedantId(undefined);
              }}
            >
              <X className="w-5 h-5 text-red-500" />
            </button>
          </>
        ) : (
          <button
            onClick={() => setIsAdding(true)}
            className="flex items-center gap-1 mr-1 text-xs text-primary"
          >
            <Plus className="w-4 h-4" /> Add Cedant
          </button>
        )}
      </div>
    </CollapsibleSection>
  );
};
